// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { AppConstants, LMTimeStampDirective, LMTransformOptions } from '@utils/index';
import { LMLoggerService } from '@services/lm-logger.service';
import { LMFieldUtilService } from './lm-field-util.service';
import { LMLocaleUtilService } from '@utils/lm-locale-util.service';
import { LMModel, LMModelField } from '@form/model/';
import { inRange } from 'lodash-es';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { LMUrlUtilService } from './lm-url-util.service';
import { LMContextService } from '@services/lm-context.service';
import { DateTime } from 'luxon';
import DOMPurify from 'dompurify';

@Injectable()
export class LMFormatUtilService {
  // see DateYMD.java
  public readonly minDate: Date = new Date(1799, 11, 31, 23, 59, 59, 999); // December 31, 1799 23:59:59.999
  public readonly maxDate: Date = new Date(2200, 0, 1, 0, 0, 0, 0);	// January 1, 2200 0:0:0.0

  public ratingOptions: Array<LMStarRatingOption> = [ {
    id: '_one-star',
    label: this.formatMessage(Soho.Locale.translate('RatingOptionLabel'), [ 1, 5 ]),
    class: '',
    href: ''
  }, {
    id: '_two-star',
    label: this.formatMessage(Soho.Locale.translate('RatingOptionLabel'), [ 2, 5 ]),
    class: '',
    href: ''
  }, {
    id: '_three-star',
    label: this.formatMessage(Soho.Locale.translate('RatingOptionLabel'), [ 3, 5 ]),
    class: '',
    href: ''
  }, {
    id: '_four-star',
    label: this.formatMessage(Soho.Locale.translate('RatingOptionLabel'), [ 4, 5 ]),
    class: '',
    href: ''
  }, {
    id: '_five-star',
    label: this.formatMessage(Soho.Locale.translate('RatingOptionLabel'), [ 5, 5 ]),
    class: '',
    href: ''
  } ];
  private readonly rangeDisplaySeparator = ' - ';

  constructor(
    public _logger: LMLoggerService,
    private domSanitizer: DomSanitizer,
    public lmFieldUtilService: LMFieldUtilService,
    public lmLocaleUtilService: LMLocaleUtilService,
    public lmUrlUtilService: LMUrlUtilService,
    public lmContextService: LMContextService,
  ) { }

  public convertNewLine(params: any) {
    // params = value
    const value = params.value;
    return value.replace(/\n/g, '<br>');
  }

  public convertSoftHyphen(value: string) {
    return value ? value.replace('\u00AD', '-') : value;
  }

  public removeNonPrintableCharacters(value: string) {
    if (!value) {
      return value;
    }
    // Form feed 000C
    // Start of Text 0002
    // Zero width space 200B
    // Match one or more occurrence of the above characters and set it to blank string
    // TODO: Check for a range of non-printable characters here?
    const regex = /\u000C+|\u0002+|\u200B+/g;
    return value.replace(regex, '');
  }

  public isCompoundField(dvField: LMField, isMultiSelect?: boolean) {
    isMultiSelect = isMultiSelect || false;
    return !isMultiSelect && dvField.hasOwnProperty('type') && (dvField.type === 'group' || dvField.type === 'array');
  }

  public isDate(date: Date) {
    if (!date || (date && date.toString().toLowerCase() === 'invalid date')) {
      return false;
    }

    return true;
  }

  // This method handles the single and multi select cases
  public stateLabelToText(states: Array<LMStateValue>, valueParam: boolean | string | Array<string>): string {
    if (states && states.length > 0) {
      const value: string | Array<string> = (typeof valueParam === 'boolean')
        ? valueParam.toString()
        : valueParam;

      return states.filter((state) => {
        if (Array.isArray(value)) {
          const isStateSelected = value.some((selectedValue: number | string) => {
            return selectedValue == state.value;
          });

          if (isStateSelected) {
            return state;
          }
        } else {
          // using non-strict equality to evaluate 0, '0', 0.00, '0.00' as zero
          // eslint-disable-next-line eqeqeq
          if (value == state.value) {
            return state;
          }
        }
      }).map((state) => state.label).join(', ');
    }

    return '';
  }

  // Form Field Transformation Rules

  // Rule for BCD for dataView value
  private bcdDataRule(value: number, options: any): string {

    // change non-numeric to zero
    if (isNaN(value)) {
      value = 0;
    }

    return Soho.Locale.formatNumber(value, options);
  }

  // Rule for BCD for display value
  private bcdDisplayRule(value: number | string, options: any, type: string, displayZero?: boolean): string {

    // change non-numeric to zero
    // isNaN can take a string even though the typedef says it can only take a number.
    if (isNaN(value as any)) {
      value = 0;
    }

    if (displayZero) {
      return Soho.Locale.formatNumber(value, options);
    }

    // display blank if value is zero
    return (this.lmFieldUtilService.isBlank(type, value))
      ? ''
      : Soho.Locale.formatNumber(value, options);
  }

  // Rule for Date, Time, DateTime for dataView value
  private dateTimeDataRule(date: Date, toPattern: string, defaultValue: string, sohoParseDateOptions?: SohoLocaleParseDateOptions): string {
    // if not a valid date, return the default value
    if (!this.isDate(date)) {
      return defaultValue;
    }

    // TODO: need typings updated for Soho.Locale; use Soho.Locale.isIslamic() for the ternary below
    const formatDateAttribs: any = {
      pattern:      toPattern,
      locale:       this.lmLocaleUtilService.name,
      calendarName: this.lmLocaleUtilService.calendarName,
      toGregorian:  (this.isIslamic(sohoParseDateOptions)) ? true : undefined
    };

    // TODO: this is part of supporting gregorian and umalqura (hijri) on the same form and is currently not used
    // need to re-evaluate this; commenting for now
    // if (sohoParseDateOptions && sohoParseDateOptions.locale) {
    //   formatDateAttribs.locale = sohoParseDateOptions.locale;
    // }

    // otherwise, return the appropriate Landmark format
    return Soho.Locale.formatDate(date, formatDateAttribs);
  }

  // Rule for Date, Time, DateTime for display value
  private dateTimeDisplayRule(date: Date | string, value: any, toPattern: string, defaultValue: string, type: string,
    sohoParseDateOptions?: SohoLocaleParseDateOptions, displayZero?: boolean) {

    // if not a valid date, return an empty string if value is default value
    // otherwise, return the invalid date and let validation rule display the error
    if (!this.isDate(date as Date)) {
      if (type === 'TIME' && displayZero) {
        const midnightDate = new Date();
        midnightDate.setHours(0, 0, 0, 0);
        date = midnightDate;
      } else {
        return (value === defaultValue || date === undefined)
            ? ''
            : value;
      }
    }

    // display blank if value is zero
    if (this.lmFieldUtilService.isBlank(type, value) && !displayZero) {
      return '';
    }

    // TODO: need typings updated for Soho.Locale; use Soho.Locale.isIslamic() for the ternary below
    const formatDateAttribs: any = {
      pattern:       toPattern,
      fromGregorian: (this.isIslamic(sohoParseDateOptions)) ? true : undefined
    };

    // TODO: this is part of supporting gregorian and umalqura (hijri) on the same form and is currently not used
    // need to re-evaluate this; commenting for now
    // if (sohoParseDateOptions && sohoParseDateOptions.locale) {
    //   formatDateAttribs.locale = sohoParseDateOptions.locale;
    // }

    return Soho.Locale.formatDate(date, formatDateAttribs);
  }

  // Rule for NUMERIC for dataView value
  private numericDataRule(value: number, options: any): number {

    // change non-numeric to zero
    if (isNaN(value)) {
      value = 0;
    }

    return value;
  }

  // Rule for NUMERIC for display value
  private numericDisplayRule(value: number | string, options: any, type: string, displayZero?: boolean): string {

    // value maybe a string when it exceeds the maximum integer value for a number
    // change non-numeric to zero
    if (typeof value !== 'string' && isNaN(value)) {
      value = 0;
    }

    if (displayZero) {
      return Soho.Locale.formatNumber(value, options);
    }

    // display blank if value is zero
    return (this.lmFieldUtilService.isBlank(type, value))
      ? ''
      : Soho.Locale.formatNumber(value, options);
  }

  // Rule for PERCENT for dataView value
  private percentDataRule(value: number | any, hasStateValues: boolean, options: any): string {

    // PERCENT as a state: remove the percent sign and return the value without formatting
    if (hasStateValues) {
      const re = new RegExp('\\' + Soho.Locale.numbers().percentSign, 'g');
      value = value.toString().replace(re, '');

      return value;
    }

    if (isNaN(value)) {
      value = 0;
    } else {
      value = value / 100;
    }

    return Soho.Locale.formatNumber(value, options);
  }

  // Rule for PERCENT for display value
  private percentDisplayRule(
    value: number | any,
    hasStateValues: boolean,
    options: any,
    type: string,
    displayZero: boolean
  ): string {

    // PERCENT as a state: keep value, do not change non-numeric to zero
    if (!hasStateValues) {
      if (isNaN(value)) {
        value = 0;
      }
    }

    if (!displayZero && this.lmFieldUtilService.isBlank(type, value)) {
      return '';
    }

    return Soho.Locale.formatNumber(value, options);
  }

  // Form Field Transformations

  /**
   * Transforms localized data into Landmark data
   * @param value
   * @param type
   * @param transformOptions - optional
   * @returns
   */
  public toDataValue(value: any, type: string, transformOptions?: LMTransformOptions): any {
    if (type === AppConstants.YYYYMMDD) {
      return this.yyyymmddToData(value, transformOptions);

    } else if (type === AppConstants.YYYYMM) {
      return this.yyyymmToData(value, transformOptions);

    } else if (type === AppConstants.MMDD) {
      return this.mmddToData(value, transformOptions);

    } else if (type === AppConstants.YYYY) {
      return this.yyyyToData(value, transformOptions);

    } else if (type === AppConstants.TIME) {
      return this.timeToData(value, transformOptions);

    } else if (type === AppConstants.TIMESTAMP) {
      return this.timestampToData(value, transformOptions);

    } else if (type === AppConstants.NUMERIC) {
      return this.numericToData(value, transformOptions);

    } else if (type === AppConstants.BCD) {
      return this.bcdToData(value, transformOptions);

    } else if (type === AppConstants.PERCENT) {
      return this.percentToData(value, transformOptions);

    } else if (type === AppConstants.ALPHA || type === AppConstants.ALPHALOWER
      || type === AppConstants.ALPHAUPPER || type === AppConstants.ALPHARIGHT) {
      // Changes may also be necessary for the list in lm-alpha.serializer.ts. Required
      // to keep the handling of special and non-printable characters consistent.
      value = this.convertSoftHyphen(value);
      value = this.removeNonPrintableCharacters(value);
      return value;
    }

    return value;
  }

  public toDisplayValueWithSpec(value: any, specField: LMSpecField, options?: LMTransformOptions): any {
    const transformOptions = options || {};
    if (specField.type === AppConstants.BOOLEAN) {
      return this.booleanToData(value);

    } else {
      // NOTE: EXCEPTION - state field values are not transformed in the model; they need to use the data value
      // because the value on each member/option also uses the data value; cast to a String to allow strict equality
      if (specField.stateValues || specField.hasUserDefinedStates) {
        return String(value);

      } else if (transformOptions.isCompoundField) {
        return value;

      } else {
        if (specField.decSize && (options === undefined || !options.hasOwnProperty('decSize'))) {
          transformOptions.decSize = specField.decSize;
        }
      }
    }
    return this.toDisplayValue(value, specField.type, transformOptions);
  }


  /**
   * Transforms Landmark data into localized data
   * @param value
   * @param type
   * @param transformOptions - optional
   * @returns
   */
  public toDisplayValue(value: any, type: string, transformOptions?: LMTransformOptions): any {
    // The Angular field templates will take care of sanitizing the value.
    // Hoping we do not need it here.
    // value = this.domSanitizer.sanitize(SecurityContext.HTML, value);

    if (type === AppConstants.YYYYMMDD) {
      return this.yyyymmddToDisplay(value, transformOptions);

    } else if (type === AppConstants.YYYYMM) {
      return this.yyyymmToDisplay(value, transformOptions);

    } else if (type === AppConstants.MMDD) {
      return this.mmddToDisplay(value, transformOptions);

    } else if (type === AppConstants.YYYY) {
      return this.yyyyToDisplay(value, transformOptions);

    } else if (type === AppConstants.TIME) {
      return this.timeToDisplay(value, transformOptions);

    } else if (type === AppConstants.TIMESTAMP) {
      return this.timestampToDisplay(value, transformOptions);

    } else if (type === AppConstants.NUMERIC) {
      if (transformOptions?.allowMultiSelections && Array.isArray(value)) {
        return value.map((val) => this.numericToDisplay(val, transformOptions)).join(AppConstants.MULTI_SEARCH_TERM_DELIMITER + ' ');
      } else {
        return this.numericToDisplay(value, transformOptions);
      }

    } else if (type === AppConstants.BCD) {
      if (transformOptions?.allowMultiSelections && Array.isArray(value)) {
        return value.map((val) => this.bcdToDisplay(val, transformOptions)).join(AppConstants.MULTI_SEARCH_TERM_DELIMITER + ' ');
      } else {
        return this.bcdToDisplay(value, transformOptions);
      }

    } else if (type === AppConstants.PERCENT) {
      if (transformOptions?.allowMultiSelections && Array.isArray(value)) {
        return value.map((val) => this.percentToDisplay(val, transformOptions)).join(AppConstants.MULTI_SEARCH_TERM_DELIMITER + ' ');
      } else {
        return this.percentToDisplay(value, transformOptions);
      }
    } else if (type === AppConstants.BOOLEAN) {
      return this.booleanToDisplay(value);

    }

    // For type ALPHA*, return empty string if value is evaluated as blank else return original value.
    return this.lmFieldUtilService.isBlank(type, value) ? '' : value;
  }

  // transform a BCD from the current locale format to the Landmark format
  public bcdToData(value: any, transformOptions?: LMTransformOptions): string {
    const decSize: number = (transformOptions && transformOptions.decSize) || 0;
    const precision: number = (transformOptions && transformOptions.precision) || 0;

    // MUST use Soho.Locale.parseNumber() as value is in the Locale format
    const bcd: number = Soho.Locale.parseNumber(value);

    // always use the smallest value
    const fractionDigits: number = (precision < decSize)
      ? precision
      : decSize;

    const options: any = {
      style:                 'decimal',
      round:                 true,
      group:                 this.lmLocaleUtilService.group,
      decimal:               this.lmLocaleUtilService.decimal,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits
    };

    const returnValue: string = this.bcdDataRule(bcd, options);
    // this._logger.log(['bcdToData', 'decSize=' + decSize, 'precision=' + precision, value, bcd, returnValue]);
    return returnValue;
  }

  // transform a BCD from the Landmark format to the current locale format
  public bcdToDisplay(value: string, transformOptions?: LMTransformOptions): string {
    const decSize: number = (transformOptions && transformOptions.decSize) || 0;
    const precision: number = (transformOptions && transformOptions.precision) || 0;
    let displayZero: boolean = (transformOptions && transformOptions.displayZero) || false;
    const showPercentSymbol: boolean = (transformOptions && transformOptions.showPercentSymbol) || false;
    const currencySymbolIs: string = (transformOptions && transformOptions.currencySymbolIs) || '';

    // always use the smallest value
    const fractionDigits: number = (precision < decSize)
      ? precision
      : decSize;

    const options: any = {
      style:                 'decimal',
      round:                 true,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
      parseNumber:           false
    };

    // allow multi is only set for search forms, allow 0 to display
    if (transformOptions?.allowMultiSelections && value !== '') {
      displayZero = true;
    }

    let returnValue: string = this.bcdDisplayRule(value, options, AppConstants.BCD, displayZero);
    // this._logger.log(['bcdToDisplay', 'decSize=' + decSize, 'precision=' + precision, bcd, value, returnValue]);

    returnValue = this.displayAmountRelative(returnValue, transformOptions);
    returnValue = this.displayNegativeParens(returnValue, transformOptions);

    if (showPercentSymbol && returnValue !== '') {
      const percentSign = !Soho.Locale.currentLocale.data.numbers ? AppConstants.PERCENT_SIGN : Soho.Locale.currentLocale.data.numbers.percentSign;
      let percentFormat = !Soho.Locale.currentLocale.data.numbers ? AppConstants.PERCENT_FORMAT : Soho.Locale.currentLocale.data.numbers.percentFormat;
      percentFormat = percentFormat.replace('¤', percentSign);
      returnValue = percentFormat.replace('###', returnValue);
    }

    if (currencySymbolIs !== '' && returnValue !== '') {
      returnValue = currencySymbolIs + returnValue;
    }

    return returnValue;
  }

  public bcdToFilterDisplay(value: string, transformOptions?: LMTransformOptions): string {
    return this.bcdToDisplay(value, transformOptions);
  }

  // transform a NUMERIC from the current locale format to the Landmark format
  public numericToData(value: any, transformOptions?: LMTransformOptions): number {
    // MUST use Soho.Locale.parseNumber() as value is in the Locale format
    const numeric: number = (typeof value === 'number')
      ? value
      : Soho.Locale.parseNumber(value.toString());

    const options: any = {
      style: 'integer',
      group: ''           // empty string means do not format with thousands character
    };

    const returnValue: number = this.numericDataRule(numeric, options);
    // this._logger.log(['numericToData', value, numeric, returnValue]);
    return returnValue;
  }

  // transform a NUMERIC from the Landmark format to the current locale format
  public numericToDisplay(value: any, transformOptions?: LMTransformOptions): string {
    let displayZero: boolean = (transformOptions && transformOptions.displayZero) || false;
    const displayThousandsCharacter: boolean = (transformOptions && transformOptions.displayThousandsCharacter) || false;
    let numeric;

    // Does the value exceed the maximum integer value for a number?
    if (!isNaN(value) && Math.abs(value) > Number.MAX_SAFE_INTEGER) {
      numeric = value;
    } else {
      // need to use parseInt() here as value is in Landmark format (en-US)
      // if Soho.Locale.parseNumber() is used and the Locale is not en-US, then Soho.Locale.parseNumber() would return
      // an incorrect value if the decimal and group separators are different than en-US
      numeric = parseInt(value, 10);  // DO NOT USE > Soho.Locale.parseNumber(value);
    }

    const options: any = {
      style: 'integer'
    };

    if (!displayThousandsCharacter) {
      options.group = '';   // empty string means do not format with thousands character
    }

    // allow multi is only set on search forms, so allow 0
    if (transformOptions?.allowMultiSelections && value !== '') {
      displayZero = true;
    }

    let returnValue: string = this.numericDisplayRule(numeric, options, AppConstants.NUMERIC, displayZero);
    // this._logger.log(['numericToDisplay', value, numeric, returnValue]);

    returnValue = this.displayAmountRelative(returnValue, transformOptions);
    returnValue = this.displayNegativeParens(returnValue, transformOptions);

    return returnValue;
  }

  // transform a PERCENT from the current locale format to the Landmark format
  public percentToData(value: any, transformOptions?: LMTransformOptions): string {
    const decSize: number = (transformOptions && transformOptions.decSize) || 0;
    const hasStateValues: boolean = (transformOptions && transformOptions.hasStateValues) || false;

    // MUST use Soho.Locale.parseNumber() as value is in the Locale format
    const percent: number | any = (hasStateValues) ? value : Soho.Locale.parseNumber(value);

    // always use decSize for dataView
    const fractionDigits: number = decSize;

    const options: any = {
      style:                 'decimal',
      round:                 true,
      group:                 this.lmLocaleUtilService.group,
      decimal:               this.lmLocaleUtilService.decimal,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits
    };

    const returnValue: string = this.percentDataRule(percent, hasStateValues, options);
    // this._logger.log(['percentToData', 'decSize=' + decSize, value, percent, returnValue]);
    return returnValue;
  }

  // transform a PERCENT from the Landmark format to the current locale format
  public percentToDisplay(value: string, transformOptions?: LMTransformOptions): string {
    const precision: number = (transformOptions && transformOptions.precision) || 0;
    const hasStateValues: boolean = (transformOptions && transformOptions.hasStateValues) || false;
    let displayZero: boolean = (transformOptions && transformOptions.displayZero) || false;

    // need to use parseFloat() here as value is in Landmark format (en-US)
    // if Soho.Locale.parseNumber() is used and the Locale is not en-US, then Soho.Locale.parseNumber() would return
    // an incorrect value if the decimal and group separators are different than en-US
    const percent: number | any = (hasStateValues) ? value : parseFloat(value);  // DO NOT USE > Soho.Locale.parseNumber(value);

    // always use precision for display
    const fractionDigits: number = (precision >= 2)
      ? precision - 2
      : precision;

    const options: any = {
      style:                 'percent',
      round:                 true,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits
    };

    // allow multi is only set on search forms, so allow zero when it is set
    if (transformOptions?.allowMultiSelections && value !== '') {
      displayZero = true;
    }

    let returnValue: string = this.percentDisplayRule(percent, hasStateValues, options, AppConstants.PERCENT, displayZero);
    // this._logger.log(['percentToDisplay', 'precision=' + precision, value, percent, returnValue]);

    returnValue = this.displayNegativeParens(returnValue, transformOptions);

    return returnValue;
  }

  public percentToFilterDisplay(value: string, transformOptions?: LMTransformOptions): string {
    const percentValue = this.percentToDisplay(value, transformOptions);
    return percentValue.replace(Soho.Locale.numbers().percentSign, '');
  }

  // transform a MMDD from the current locale format to the Landmark format
  public mmddToData(value, transformOptions?: LMTransformOptions): string {
    let returnValue: string;
    const defaultValue: string = this.lmLocaleUtilService.calendar.MMDD.defaultValue;
    const toPattern: string = this.lmLocaleUtilService.calendar.MMDD.pattern;
    const fromPattern: string = Soho.Locale.calendar().dateFormat.month;

    if (value && value.indexOf(this.rangeDisplaySeparator) >= 0) {
      // date is a range
      returnValue = this.handleRangeDateToData(value, fromPattern, toPattern, defaultValue);

    } else {
      const date: Date = Soho.Locale.parseDate(value, fromPattern, false);
      returnValue = this.dateTimeDataRule(date, toPattern, defaultValue);
    }

    // this._logger.log(['mmddToData', value, returnValue]);
    return returnValue;
  }

  // transform a MMDD from the Landmark format to the current locale format
  public mmddToDisplay(value, transformOptions?: LMTransformOptions): string {
    let returnValue: string;
    const defaultValue: string = this.lmLocaleUtilService.calendar.MMDD.defaultValue;
    const fromPattern: string = this.lmLocaleUtilService.calendar.MMDD.pattern;
    const toPattern: string = Soho.Locale.calendar().dateFormat.month;

    if (value && value.indexOf(this.rangeDisplaySeparator) >= 0) {
      // date is a range
      returnValue = this.handleRangeDateToDisplay(value, fromPattern, toPattern, defaultValue, AppConstants.MMDD);

    } else {
      const landmarkParseDateOptions: SohoLocaleParseDateOptions = this.buildLandmarkParseDateOptions(fromPattern);
      const date: Date = Soho.Locale.parseDate(value, landmarkParseDateOptions, false);
      returnValue = this.dateTimeDisplayRule(date, value, toPattern, defaultValue, AppConstants.MMDD);
    }

    // this._logger.log(['mmddToDisplay', value, returnValue]);
    return returnValue;
  }

  // transform a TIME from the current locale format to the Landmark format
  public timeToData(value, transformOptions?: LMTransformOptions): string {
    const defaultValue: string = this.lmLocaleUtilService.calendar.TIME.defaultValue;
    const toPattern: string = this.lmLocaleUtilService.calendar.TIME.pattern;
    let fromPattern: string = Soho.Locale.calendar().dateFormat.timestamp;

    if (transformOptions && transformOptions.hasOwnProperty('timeStampDirective')) {
      if (transformOptions.timeStampDirective === LMTimeStampDirective.DISPLAY_HOURS_AND_MINUTES) {
        fromPattern = Soho.Locale.calendar().timeFormat;
      }
    }

    const date: Date = Soho.Locale.parseDate(value, fromPattern, false);
    const returnValue: string = this.dateTimeDataRule(date, toPattern, defaultValue);

    // this._logger.log(['timeToData', value, returnValue]);
    return returnValue;
  }

  // transform a TIME from the Landmark format to the current locale format
  public timeToDisplay(value, transformOptions?: LMTransformOptions): string {
    const defaultValue: string = this.lmLocaleUtilService.calendar.TIME.defaultValue;
    const fromPattern: string = this.lmLocaleUtilService.calendar.TIME.pattern;
    let toPattern: string = Soho.Locale.calendar().dateFormat.timestamp;
    const displayZero: boolean = (transformOptions && transformOptions.displayZero) || false;

    if (transformOptions && transformOptions.hasOwnProperty('timeStampDirective')) {
      if (transformOptions.timeStampDirective === LMTimeStampDirective.DISPLAY_HOURS_AND_MINUTES) {
        toPattern = Soho.Locale.calendar().timeFormat;
      }
    }

    const landmarkParseDateOptions: SohoLocaleParseDateOptions = this.buildLandmarkParseDateOptions(fromPattern);
    const date: Date = Soho.Locale.parseDate(value, landmarkParseDateOptions, false);
    const returnValue: string = this.dateTimeDisplayRule(date, value, toPattern, defaultValue, AppConstants.TIME, null, displayZero);

    // this._logger.log(['timeToDisplay', value, returnValue]);
    return returnValue;
  }

  // transform a TIMESTAMP from the current locale format to the Landmark format
  public timestampToData(value, transformOptions?: LMTransformOptions): string {
    const defaultValue: string = this.lmLocaleUtilService.calendar.TIMESTAMP.defaultValue;
    let fromPattern = Soho.Locale.calendar().dateFormat.short + ' ' + Soho.Locale.calendar().dateFormat.timestamp;
    const toPattern: string = this.lmLocaleUtilService.calendar.TIMESTAMP.pattern;
    let transformTime = false;
    let returnValue: string;

    if (transformOptions && transformOptions.hasOwnProperty('timeStampDirective')) {
      if (transformOptions.timeStampDirective === LMTimeStampDirective.DISPLAY_DATE) {
        fromPattern = Soho.Locale.calendar().dateFormat.short;

      } else if (transformOptions.timeStampDirective === LMTimeStampDirective.DISPLAY_HOURS_AND_MINUTES) {
        fromPattern = Soho.Locale.calendar().timeFormat;
        transformTime = true;

      } else if (transformOptions.timeStampDirective === LMTimeStampDirective.DISPLAY_TIME) {
        fromPattern = Soho.Locale.calendar().dateFormat.timestamp;
        transformTime = true;
      } else if (transformOptions.timeStampDirective === LMTimeStampDirective.DISPLAY_DATE_AND_HOURS_AND_MINUTES) {
        fromPattern = Soho.Locale.calendar().dateFormat.datetime;
      }
    }

    if (transformOptions?.forceFromPattern) {
      fromPattern = transformOptions.forceFromPattern;
    }

    // TODO: need to evaluate the locale and calendarName properties in LMTransformOptions
    const sohoParseDateOptions: SohoLocaleParseDateOptions = this.buildSohoParseDateOptions(toPattern, fromPattern, transformOptions);
    sohoParseDateOptions.locale = this.lmLocaleUtilService.name;
    sohoParseDateOptions.calendarName = this.lmLocaleUtilService.calendarName;

    let date: Date = Soho.Locale.parseDate(value, sohoParseDateOptions, false);

    if (transformOptions?.timeZone && !transformTime) {
      date = this.timeZoneDateToGMTDate(date, transformOptions.timeZone, transformOptions?.disableDST);
    }

    if (value && value.indexOf(this.rangeDisplaySeparator) >= 0) {
      returnValue = this.handleRangeTimestampToData(value, fromPattern, toPattern, defaultValue, sohoParseDateOptions, transformTime, transformOptions);
    } else {
      returnValue = this.timestampDataRule(date, fromPattern, toPattern, defaultValue, sohoParseDateOptions, transformTime, transformOptions);
    }

    if (this.isIslamic(sohoParseDateOptions)) {
      // need to transform the umalqura value into en-US
      // example:
      // 20200701090000 becomes                       > 9:00:00 1441/11/10 for display in umalqura
      // if that is changed to today, 07/17/2020:
      // the display value becomes                    > 9:13:26 1441/11/26
      // so the let date variable above becomes       > Fri Nov 26 1441 09:13:26 GMT-0550 (Central Standard Time)
      // we need to take that date and format it into
      // en-US gregorian using the same pattern       > 2020/07/17 9:13:26 AM
      // and then parse the date to get a date object > Fri Jul 17 2020 09:13:26 GMT-0500 (Central Daylight Time)
      // which we can use for our manipulation of the date to get a Landmark formatted date value
      const convertedDate = this.dateTimeDataRule(date, fromPattern, defaultValue, sohoParseDateOptions);
      date = Soho.Locale.parseDate(convertedDate, sohoParseDateOptions, false);
    }
    return returnValue;
  }

  private timeZoneDateToGMTDate(date: Date, timeZone: string, disableDST?: boolean): Date {
    if (!date) {
      return;
    }

    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const hour =  date.getHours();
    const minute = date.getMinutes();
    const second = date.getSeconds();

    let dateTime = DateTime.fromObject({ year, month, day, hour, minute, second });
    const dateTimeWithTimeZone = DateTime.fromObject({ year, month, day, hour, minute, second }, { zone: timeZone });
    const dateTimeLocal = DateTime.fromObject({ year, month, day, hour, minute, second }, { zone: 'local' });

    let standardOffset = 0;
    let nonDSTOffset = 0;
    const offsetLocal = dateTimeLocal.offset;

    if (disableDST) {
      nonDSTOffset = this.getStandardOffset(dateTimeWithTimeZone);
      dateTime = dateTime.minus({ minutes: nonDSTOffset }).plus({ minutes: offsetLocal });
    } else {
      standardOffset = dateTimeWithTimeZone.offset;
      dateTime = dateTime.minus({ minutes: standardOffset }).plus({ minutes: offsetLocal });
    }

    return dateTime.toJSDate();
  }

  private timestampDataToTimeZoneDateTime(timeStampDataValue: string, transformOptions: LMTransformOptions): DateTime {

    const year: number = parseInt(timeStampDataValue.substring(0, 4), 10);
    const month: number = parseInt(timeStampDataValue.substring(4, 6), 10) - 1;
    const day: number = parseInt(timeStampDataValue.substring(6, 8), 10);
    const hr: number = parseInt(timeStampDataValue.substring(8, 10), 10);
    const mn: number = parseInt(timeStampDataValue.substring(10, 12), 10);
    const sc: number = parseInt(timeStampDataValue.substring(12, 14), 10);


    let dateTime = DateTime.utc(year, month+1, day, hr, mn, sc);
    let standardOffset = 0;
    let nonDSTOffset = 0;

    // DateTime can't handle invalid parameters
    if (!dateTime.isValid) {
      return undefined;
    }

    const dateTimeLocal = DateTime.fromObject({ year, month: month+1, day, hour: hr, minute: mn, second: sc }, { zone: 'local' });
    dateTime = dateTime.minus({ minutes: dateTimeLocal.offset });

    if (transformOptions?.timeZone) {
      const dateTimeWithTimeZone = DateTime.fromObject({ year, month: month+1, day, hour: hr, minute: mn, second: sc }, { zone: transformOptions.timeZone });
      nonDSTOffset = this.getStandardOffset(dateTimeWithTimeZone);
      standardOffset = dateTimeWithTimeZone.offset;
    } else {
      nonDSTOffset = this.getStandardOffset(dateTimeLocal);
      standardOffset = dateTimeLocal.offset;
    }

    if (transformOptions?.disableDST) {
      dateTime = dateTime.plus({ minutes: nonDSTOffset });
    } else {
      dateTime = dateTime.plus({ minutes: standardOffset });
    }

    return dateTime;
  }

  private timestampDataRule(date: Date, fromPattern: string, toPattern: string, defaultValue: string,
    sohoParseDateOptions?: SohoLocaleParseDateOptions, transformTime?: boolean, transformOptions?: LMTransformOptions): string {

    if (!this.isDate(date)) {
      return defaultValue;
    }

    if (transformOptions?.timeStampDirective) {
      // for non-empty timeStampDataValue i.e. update
      if (transformOptions?.timeStampDataValue) {
        const dateTime = this.timestampDataToTimeZoneDateTime(transformOptions.timeStampDataValue, transformOptions);
        const adjustDate: Date = dateTime.toJSDate();
        
        if (transformTime) {
          // set the hours, minutes, seconds on to the adjusted date
          adjustDate.setHours(date.getHours());
          adjustDate.setMinutes(date.getMinutes());
          adjustDate.setSeconds(date.getSeconds());
        } else if (transformOptions.timeStampDirective === LMTimeStampDirective.DISPLAY_DATE) {
          adjustDate.setFullYear(date.getFullYear());
          adjustDate.setMonth(date.getMonth());
          adjustDate.setDate(date.getDate());
        }
        // re-evaluate the new adjustDate time to apply timezone
        // soho can't detect Regional Format so we instead force the fromPattern to standard format M/d/yyyy h:mm:ss a. This applies to every regional format.
        const localeStringDateTime = adjustDate.toLocaleString("en-US");
        const localeStringTime = localeStringDateTime.split(',')?.[1];
        const localeStringDate = [adjustDate.getMonth() + 1, adjustDate.getDate(), adjustDate.getFullYear()].join('/');
        const dateValue = localeStringDate + localeStringTime; 
        return this.timestampToData(dateValue, { timeZone: transformOptions.timeZone, disableDST: transformOptions.disableDST, forceFromPattern: 'M/d/yyyy h:mm:ss a'});
      }

      // for empty timeStampDataValue i.e. create
      if (!transformOptions?.timeStampDataValue && transformTime) {
        let adjustDate: Date;
        if (transformOptions.timeStampDataValue === this.lmLocaleUtilService.calendar.TIMESTAMP.defaultValue) {
          // need current year, month, day to add the time parts
          adjustDate = new Date();
        } 

        adjustDate.setHours(date.getHours());
        adjustDate.setMinutes(date.getMinutes());
        adjustDate.setSeconds(date.getSeconds());

        date = adjustDate;
      }
    }

    // this.dateTimeDataRule is not used for TIMESTAMP as the value is formatted because it needs to be in UTC format
    const returnValue = ('0' + date.getUTCFullYear()).slice(-4)
      + ('0' + (date.getUTCMonth() + 1)).slice(-2)
      + ('0' + date.getUTCDate()).slice(-2)
      + ('0' + date.getUTCHours()).slice(-2)
      + ('0' + date.getUTCMinutes()).slice(-2)
      + ('0' + date.getUTCSeconds()).slice(-2)
      + '00';
    return returnValue;
  }

  private handleRangeTimestampToData(value: string, fromPattern: string, toPattern: string, defaultValue: string,
    sohoParseDateOptions?: SohoLocaleParseDateOptions, transformTime?: boolean, transformOptions?: LMTransformOptions): string {

    const returnValue = value.split(this.rangeDisplaySeparator)
      .map((dateValue) => {
        const date: Date = Soho.Locale.parseDate(dateValue, sohoParseDateOptions, false);
        return this.timestampDataRule(date, fromPattern, toPattern, defaultValue, sohoParseDateOptions, transformTime, transformOptions);
      })
      .join(this.rangeDisplaySeparator);

    return returnValue;
  }

  private handleRangeTimestampToDisplay(value: string, fromPattern: string, toPattern: string, defaultValue: string, type: string,
    sohoParseDateOptions?: SohoLocaleParseDateOptions): string {

    const returnValue = value.split(this.rangeDisplaySeparator)
      .map((dateValue) => {
        const year: number = parseInt(dateValue.substr(0, 4), 10);
        const month: number = parseInt(dateValue.substr(4, 2), 10) - 1;
        const day: number = parseInt(dateValue.substr(6, 2), 10);
        const hr: number = parseInt(dateValue.substr(8, 2), 10);
        const mn: number = parseInt(dateValue.substr(10, 2), 10);
        const sc: number = parseInt(dateValue.substr(12, 2), 10);

        const date: Date = new Date(Date.UTC(year, month, day, hr, mn, sc));
        return this.dateTimeDisplayRule(date, dateValue, toPattern, defaultValue, type, sohoParseDateOptions);
      })
      .join(this.rangeDisplaySeparator);

    return returnValue;
  }

  // transform a TIMESTAMP from the Landmark format to the current locale format
  public timestampToDisplay(value, transformOptions?: LMTransformOptions): string {
    const defaultValue: string = this.lmLocaleUtilService.calendar.TIMESTAMP.defaultValue;
    let fromPattern: string = this.lmLocaleUtilService.calendar.TIMESTAMP.pattern;
    let returnValue: string;

    let toPattern: string = Soho.Locale.calendar().dateFormat.short + ' ' + Soho.Locale.calendar().dateFormat.timestamp;

    if (transformOptions && transformOptions.hasOwnProperty('timeStampDirective')) {
      if (transformOptions.timeStampDirective === LMTimeStampDirective.DISPLAY_DATE) {
        toPattern = Soho.Locale.calendar().dateFormat.short;

      } else if (transformOptions.timeStampDirective === LMTimeStampDirective.DISPLAY_HOURS_AND_MINUTES) {
        toPattern = Soho.Locale.calendar().timeFormat;

      } else if (transformOptions.timeStampDirective === LMTimeStampDirective.DISPLAY_TIME) {
        toPattern = Soho.Locale.calendar().dateFormat.timestamp;
      } else if (transformOptions.timeStampDirective === LMTimeStampDirective.DISPLAY_DATE_AND_HOURS_AND_MINUTES) {
        toPattern = Soho.Locale.calendar().dateFormat.datetime;
      }
    }

    const sohoParseDateOptions: SohoLocaleParseDateOptions = this.buildSohoParseDateOptions(fromPattern, toPattern, transformOptions);
    if (value && value.indexOf(this.rangeDisplaySeparator) >= 0) {
      // date is a range
      fromPattern = `${fromPattern}${this.rangeDisplaySeparator}${fromPattern}`;
      returnValue = this.handleRangeTimestampToDisplay(value, fromPattern, toPattern, defaultValue, AppConstants.TIMESTAMP, sohoParseDateOptions);
    } else {
      const dateTime = this.timestampDataToTimeZoneDateTime(value, transformOptions);
      returnValue = this.dateTimeDisplayRule(dateTime?.toISO(), value, toPattern, defaultValue, AppConstants.TIMESTAMP, sohoParseDateOptions);

      if (transformOptions?.displayWithTimeZone) {
        if (returnValue) {
          returnValue += ` ${this.getOffsetName(dateTime, 'short', transformOptions?.disableDST)}`;
        }
      }
    }
    return returnValue;
  }

  /** gbugauisan
   * function to get the non-dst offset of a specified timezone
   * @param dateTime
   * @returns {number} the standard non-dst offset
   */
  public getStandardOffset(dateTime: DateTime) {
    if (dateTime.isInDST) {
      do {
        dateTime = dateTime.plus( { month: 2 } );
      }
      while(dateTime.isInDST);
    }

    return dateTime.offset;
  }

  public getOffsetName(dateTime: DateTime, format: 'long'|'short' = 'short', disableDST = false) {
    if (disableDST) {
      if (dateTime.isInDST) {
        do {
          dateTime = dateTime.plus( { month: 2 } );
        }
        while(dateTime.isInDST);
      }
    }
    return format === 'short' ? dateTime.offsetNameShort : dateTime.offsetNameLong;
  }

  // transform a YYYY from the current locale format to the Landmark format
  public yyyyToData(value, optitransformOptionsons?: LMTransformOptions): string {
    let returnValue: string;
    const defaultValue: string = this.lmLocaleUtilService.calendar.YYYY.defaultValue;
    const toPattern: string = this.lmLocaleUtilService.calendar.YYYY.pattern;
    const fromPattern = 'yyyy';

    if (value && value.indexOf(this.rangeDisplaySeparator) >= 0) {
      // date is a range
      returnValue = this.handleRangeDateToData(value, fromPattern, toPattern, defaultValue);

    } else {
      const date: Date = Soho.Locale.parseDate(value, fromPattern, false);
      returnValue = this.dateTimeDataRule(date, toPattern, defaultValue);
    }

    // this._logger.log(['yyyyToData', value, returnValue]);
    return returnValue;
  }

  // transform a YYYY from the Landmark format to the current locale format
  public yyyyToDisplay(value, transformOptions?: LMTransformOptions): string {
    let returnValue: string;
    const defaultValue: string = this.lmLocaleUtilService.calendar.YYYY.defaultValue;
    const fromPattern: string = this.lmLocaleUtilService.calendar.YYYY.pattern;
    const toPattern = 'yyyy';

    if (value && value.indexOf(this.rangeDisplaySeparator) >= 0) {
      // date is a range
      returnValue = this.handleRangeDateToDisplay(value, fromPattern, toPattern, defaultValue, AppConstants.YYYY);

    } else {
      const landmarkParseDateOptions: SohoLocaleParseDateOptions = this.buildLandmarkParseDateOptions(fromPattern);
      const date: Date = Soho.Locale.parseDate(value, landmarkParseDateOptions, false);
      returnValue = this.dateTimeDisplayRule(date, value, toPattern, defaultValue, AppConstants.YYYY);
    }

    // this._logger.log(['yyyyToDisplay', value, returnValue]);
    return returnValue;
  }

  // transform a YYYYMM from the current locale format to the Landmark format
  public yyyymmToData(value, transformOptions?: LMTransformOptions): string {
    let returnValue: string;
    const defaultValue: string = this.lmLocaleUtilService.calendar.YYYYMM.defaultValue;
    const toPattern: string = this.lmLocaleUtilService.calendar.YYYYMM.pattern;
    const fromPattern: string = Soho.Locale.calendar().dateFormat.year;

    if (value && value.indexOf(this.rangeDisplaySeparator) >= 0) {
      // date is a range
      returnValue = this.handleRangeDateToData(value, fromPattern, toPattern, defaultValue);

    } else {
      const date: Date = Soho.Locale.parseDate(value, fromPattern, false);
      returnValue = this.dateTimeDataRule(date, toPattern, defaultValue);
    }

    // this._logger.log(['yyyymmToData', value, returnValue]);
    return returnValue;
  }

  // transform a YYYYMM from the Landmark format to the current locale format
  public yyyymmToDisplay(value, transformOptions?: LMTransformOptions): string {
    let returnValue: string;
    const defaultValue: string = this.lmLocaleUtilService.calendar.YYYYMM.defaultValue;
    const fromPattern: string = this.lmLocaleUtilService.calendar.YYYYMM.pattern;
    const toPattern: string = Soho.Locale.calendar().dateFormat.year;

    if (value && value.indexOf(this.rangeDisplaySeparator) >= 0) {
      // date is a range
      returnValue = this.handleRangeDateToDisplay(value, fromPattern, toPattern, defaultValue, AppConstants.YYYYMM);

    } else {
      const landmarkParseDateOptions: SohoLocaleParseDateOptions = this.buildLandmarkParseDateOptions(fromPattern);
      const date: Date = Soho.Locale.parseDate(value, landmarkParseDateOptions, false);
      returnValue = this.dateTimeDisplayRule(date, value, toPattern, defaultValue, AppConstants.YYYYMM);
    }

    // this._logger.log(['yyyymmToDisplay', value, returnValue]);
    return returnValue;
  }

  // transform a YYYYMMDD from the current locale format to the Landmark format
  public yyyymmddToData(value, transformOptions?: LMTransformOptions): string {
    let returnValue: string;
    const defaultValue: string = this.lmLocaleUtilService.calendar.YYYYMMDD.defaultValue;
    const toPattern: string = this.lmLocaleUtilService.calendar.YYYYMMDD.pattern;
    const fromPattern: string = Soho.Locale.calendar().dateFormat.short;
    const sohoParseDateOptions: SohoLocaleParseDateOptions = this.buildSohoParseDateOptions(toPattern, fromPattern, transformOptions);

    if (value && value.indexOf(this.rangeDisplaySeparator) >= 0) {
      // date is a range
      returnValue = this.handleRangeDateToData(value, fromPattern, toPattern, defaultValue, sohoParseDateOptions);

    } else {
      const landmarkParseDateOptions: SohoLocaleParseDateOptions = this.buildLandmarkParseDateOptions(fromPattern);
      const date: Date = Soho.Locale.parseDate(value, landmarkParseDateOptions, false);
      returnValue = this.dateTimeDataRule(date, toPattern, defaultValue, sohoParseDateOptions);
    }

    // this._logger.log(['yyyymmddToData', value, returnValue]);
    return returnValue;
  }

  // transform a YYYYMMDD from the Landmark format to the current locale format
  public yyyymmddToDisplay(value, transformOptions?: LMTransformOptions): string {
    let returnValue: string;
    const defaultValue: string = this.lmLocaleUtilService.calendar.YYYYMMDD.defaultValue;
    const fromPattern: string = this.lmLocaleUtilService.calendar.YYYYMMDD.pattern;
    let toPattern: string = Soho.Locale.calendar().dateFormat.short;

    if (transformOptions?.forceToPattern) {
      toPattern = transformOptions?.forceToPattern;
    }

    const sohoParseDateOptions: SohoLocaleParseDateOptions = this.buildSohoParseDateOptions(fromPattern, toPattern, transformOptions);

    if (value && value.indexOf(this.rangeDisplaySeparator) >= 0) {
      // date is a range
      returnValue = this.handleRangeDateToDisplay(value, fromPattern, toPattern, defaultValue, AppConstants.YYYYMMDD, sohoParseDateOptions);
    } else {
      const landmarkParseDateOptions: SohoLocaleParseDateOptions = this.buildLandmarkParseDateOptions(fromPattern);
      const date: Date = Soho.Locale.parseDate(value, landmarkParseDateOptions, false);
      returnValue = this.dateTimeDisplayRule(date, value, toPattern, defaultValue, AppConstants.YYYYMMDD, sohoParseDateOptions);
    }

    // this._logger.log(['yyyymmddToDisplay', value, returnValue]);
    return returnValue;
  }

  // transform a YYYYMMDD from the Landmark format to the current locale format
  public yyyymmddhhmmssToDisplay(value, transformOptions?: LMTransformOptions): string {
    let returnValue: string;
    const defaultValue: string = this.lmLocaleUtilService.calendar.YYYYMMDD.defaultValue;
    const fromPattern: string = this.lmLocaleUtilService.calendar.TIMESTAMP.pattern;
    const toPattern = 'yyyy-MM-ddTHH:mm:ss.SSS';
    const sohoParseDateOptions: SohoLocaleParseDateOptions = this.buildSohoParseDateOptions(fromPattern, toPattern, transformOptions);

    if (value.indexOf(this.rangeDisplaySeparator) >= 0) {
      // date is a range
      returnValue = this.handleRangeDateToDisplay(value, fromPattern, toPattern, defaultValue, AppConstants.YYYYMMDD, sohoParseDateOptions);

    } else {
      // TODO: need to evaluate if landmarkParseDateOptions are needed here
      // const landmarkParseDateOptions: SohoLocaleParseDateOptions = this.buildLandmarkParseDateOptions(fromPattern);
      // const date: Date = Soho.Locale.parseDate(value, landmarkParseDateOptions, false);
      const date: Date = Soho.Locale.parseDate(value, fromPattern, false);
      returnValue = this.dateTimeDisplayRule(date, value, toPattern, defaultValue, AppConstants.YYYYMMDD, sohoParseDateOptions);
    }

    // this._logger.log(['yyyymmddToDisplay', value, returnValue]);
    return returnValue;
  }

  private isIslamic(transformOptions?: LMTransformOptions): boolean {
    if (transformOptions && transformOptions.calendarName) {
      return (transformOptions.calendarName === 'islamic-umalqura');
    }

    return (Soho.Locale.calendar().name === 'islamic-umalqura');
  }

  // these are the options used internally in Soho.Locale.parseDate() when it is called from Soho.Locale.formatDate()
  private buildSohoParseDateOptions(toPattern?: string, fromPattern?: string, transformOptions?: LMTransformOptions): SohoLocaleParseDateOptions {
    const sohoParseDateOptions: SohoLocaleParseDateOptions = {};

    if (toPattern) {
      sohoParseDateOptions.pattern = toPattern;
    }

    if (fromPattern) {
      sohoParseDateOptions.dateFormat = fromPattern;
    }

    // TODO: this is part of supporting gregorian and umalqura (hijri) on the same form and is currently not used
    // need to re-evaluate this; commenting for now
    // if (transformOptions && transformOptions.locale) {
    //   sohoParseDateOptions.locale = transformOptions.locale;
    // }

    if (transformOptions && transformOptions.calendarName) {
      sohoParseDateOptions.calendarName = transformOptions.calendarName;
    }

    return sohoParseDateOptions;
  }

  // these are the options used in the ...ToDisplay() methods when we call Soho.Locale.parseDate() directly
  private buildLandmarkParseDateOptions(fromPattern: string) {
    const landmarkParseDateOptions: SohoLocaleParseDateOptions = {
      dateFormat: fromPattern,
      locale: this.lmLocaleUtilService.name,
      calendarName: this.lmLocaleUtilService.calendarName
    };

    return landmarkParseDateOptions;
  }

  private handleRangeDateToData(value: string, fromPattern: string, toPattern: string, defaultValue: string,
    sohoParseDateOptions?: SohoLocaleParseDateOptions): string {

    const returnValue = value.split(this.rangeDisplaySeparator)
      .map((dateValue) => {
        const date: Date = Soho.Locale.parseDate(dateValue, fromPattern, false);
        return this.dateTimeDataRule(date, toPattern, defaultValue, sohoParseDateOptions);
      })
      .join(this.rangeDisplaySeparator);

    return returnValue;
  }

  private handleRangeDateToDisplay(value: string, fromPattern: string, toPattern: string, defaultValue: string, type: string,
    sohoParseDateOptions?: SohoLocaleParseDateOptions): string {

    const landmarkParseDateOptions: SohoLocaleParseDateOptions = this.buildLandmarkParseDateOptions(fromPattern);
    const returnValue = value.split(this.rangeDisplaySeparator)
      .map((dateValue) => {
        const date: Date = Soho.Locale.parseDate(dateValue, landmarkParseDateOptions, false);
        return this.dateTimeDisplayRule(date, dateValue, toPattern, defaultValue, type, sohoParseDateOptions);
      })
      .join(this.rangeDisplaySeparator);

    return returnValue;
  }

  // List Cell Transformations

  // transform a BOOLEAN from the current locale format to the Landmark format
  public booleanToData(value: any): boolean {
    if (value === 'true' || value === true || value === 'Yes') {
      return true;

    } else if (value === 'false' || value === false || value === 'No') {
      return false;
    }

    return false;
  }

  // transform a BOOLEAN from the Landmark format to the current locale format
  public booleanToDisplay(value: any): string {
    if (value === 'true' || value === true) {
      return Soho.Locale.translate('Yes');

    } else if (value === 'false' || value === false) {
      return Soho.Locale.translate('No');
    }

    return value;
  }

  // need to format the zero value for BCD and PERCENT types as they need to match
  // the dataView field value for the track dirty processing to work correctly.
  // see also ViewBuilderBase.java > fieldAttributeToJsonObject()
  // and JSONConverter.java > fieldStateArrayForFieldValueNg()
  // and lm-dropdown.component.ts
  // using precision here as that is the property available for a state
  public fieldStateFormatZeroValue(type: string, precision?: number): string {
    let zeroValue;
    let transformOptions: LMTransformOptions;

    if (type === AppConstants.NUMERIC) {
      zeroValue = this.toDataValue('0', type).toString();

    } else if (type === AppConstants.BCD) {
      transformOptions = {
        decSize:   precision,
        precision
      };
      zeroValue = this.toDataValue('0', type, transformOptions).toString();

    } else if (type === AppConstants.PERCENT) {
      transformOptions = {
        decSize: precision
      };
      zeroValue = this.toDataValue('0', type, transformOptions).toString();
    }

    return zeroValue;
  }

  public fieldStatesToOptionsArray(states: LMStateValue[], addBlankEntry: boolean, zeroOrBlankValue?: string, isUserDefinedStates?: boolean, userDefinedOnSearch?: boolean, fromListRow?: boolean) {
    const optionsArr = [];
    let hasBlankValue = false;
    let label = '';

    if (states) {
      for (let i = 0, len = states.length; i < len; i++) {
        const state = states[ i ];

        if (state.label.length === 0 || state.label === ' ') {
          label = '';
        } else {
          label = state.label;
        }
        if (isUserDefinedStates) {
          hasBlankValue = false;
          // eslint-disable-next-line eqeqeq
          // using non-strict equality to evaluate 0, '0', 0.00, '0.00' as zero
          // eslint-disable-next-line eqeqeq
        } else if (state.value === '' || state.value == 0) {
          hasBlankValue = true;
        }

        optionsArr.push({
          value: String(state.value),
          label
        });
      }
    }

    if (addBlankEntry) {
      // create blank entry
      if (!hasBlankValue) {
        let item;
        if (userDefinedOnSearch) {
          item = {
            value: 'Not Entered',
            label: Soho.Locale.translate('ListStaticFieldFilterNotEntered')
          };
        } else {

          label = ' ';
          if (fromListRow) {
            label = '&nbsp;';
          }

          item = {
            value: (zeroOrBlankValue === undefined) ? '' : zeroOrBlankValue,
            label
          };
        }

        optionsArr.splice(0, 0, item);
      }
    }

    return optionsArr;
  }

  public fieldStatesToMultiOptionsArray(states: Array<any>, selectedValues: Array<any>): Array<LMStateValue> {
    const stateValues: Array<LMStateValue> = [];

    if (!Array.isArray(selectedValues)) {
      selectedValues = [ selectedValues ];
    }

    for (let index = 0, len = states.length; index < len; index++) {
      const state = states[ index ];
      const label = state.label;
      const isSelected = (selectedValues.includes(state.value)) ? true : undefined;

      stateValues.push({
        value:    String(state.value),
        label,
        selected: isSelected
      });
    }

    return stateValues;
  }

  // Used with Soho.Locale.translate when the message has variables
  public formatMessage(message: string, variables: any) {
    let text = message;
    let vars = variables;

    if (!Array.isArray(vars)) {
      vars = [ vars ];
    }

    for (let index = 0; index < vars.length; index++) {
      text = this.replaceMessageRule({ text, value: vars[ index ], index });
    }
    // The text need not be sanitized since this routine is called by Soho.Locale.translate. This may not have user input variables.
    return text;
  }

  // public formatMessageRule(messageRule:MessageRule, dataView:DataView, specFields?:{[key:string]:SpecField});
  // public formatMessageRule(messageRule:string, dataView:DataView, specFields?:{[key:string]:SpecField});

  public formatMessageRule(messageRule: any, data?: LMDataView | LMModel, specFields?: LMSpecFields | Array<LMListDisplayField>, sanitize = true): string {
    if (typeof messageRule === 'string') {
      // sanitize is not called as the messageRule is from the spec and not user input
      return messageRule as string;
    }

    if (messageRule.variables === undefined) {
      // sanitize is not called as the messageRule is from the spec and not user input
      return messageRule.resourceValue;
    }

    let text: string = messageRule.resourceValue;
    const isModel: boolean = (data instanceof LMModel);

    for (let index = 0, len = messageRule.variables.length; index < len; index++) {
      let fieldData: any = null;
      let field: LMField | LMModelField;
      let value: string | number;
      const fieldId: string = messageRule.variables[ index ].fieldId;

      if (messageRule.variables[ index ].type === 'numberLabel' && data && isModel) {
        value = (data as LMModel).numberLabel;
      } else if (messageRule.variables[ index ].type === 'field' && data) {
        field = (isModel)
          ? (data as LMModel).getField(fieldId) as LMModelField
          : (data as LMDataView).fields[ fieldId ] as LMField;

        if (!field) {
          if (!isModel) {
            const dataView = (data as LMDataView);
            if (dataView && dataView.cubeInfo && dataView.cubeInfo.titleFields) {
              field = dataView.cubeInfo.titleFields[ fieldId ];
              if (!field) {
                continue;
              }
            } else {
              continue;
            }
          } else {
            continue; // safety
          }
        }

        // LMCLIENT-6889: If the field has a label then use the label to be replaced in the message instead of value.
        value = (field.label)
          ? field.label
          : (isModel)
            ? (field as LMModelField).getData()
            : field.value;
        // Only the dataview or model value is santized. The rest of the message rule is from spec.
        // LMCLIENT-25675 SOHO code sanitizes the title span. The entities were getting html encoded twice.
        // Commenting it out here.
        // value = (sanitize) ? this.domSanitizer.sanitize(SecurityContext.HTML, value) : value;

        fieldData = {
          fieldId,
          field,
          specField: (specFields) ? specFields[ fieldId ] : null
        };
      } else if (messageRule.variables[ index ].type === 'value') {
        value = messageRule.variables[ index ].value;
      } else if (messageRule.variables[ index ].type === 'context') {
        const contextName = messageRule.variables[ index ].value;
        if (contextName === 'context_prd_actor') {
          value = this.lmContextService.getActorId();
        } else if (contextName === 'context_prd_dataarea_prd_container') {
          value = this.lmContextService.getUrlPdlOrDa();
        } else {
          value = data?.contextProperties[ contextName ];
        }
      }

      // if (fieldData && fieldData.specField && fieldData.specField !== null && fieldData.specField.isStaticStateField) {
      if (fieldData?.specField?.isStaticStateField) {
        const params = {
          value:       field.value,
          stateValues: fieldData.specField.states
        };
        text = this.findStateItem(params).label;
      } else if (value !== undefined) {
        text = this.replaceMessageRule({ text, value, index, fieldData });
      }
    }

    return text;
  }

  public replaceMessageRule(params: any) {
    // params = text, value, index, fieldData
    let text = params.text;
    const value = params.value;
    const index = params.index;
    const fieldData = params.fieldData || null;

    // MessageFormat information > http://docs.oracle.com/javase/6/docs/api/java/text/MessageFormat.html

    // TODO: //maf message rules can be nested, so there is an issue here with expression
    // looking for {0, or {0}
    const find = new RegExp('\\{' + index + ',|\\{' + index + '\\}', 'g');
    const result = text.match(find);
    if (result) {
      const len = result.length;

      for (let item = 0; item < len; item++) {
        const startPos = text.indexOf(result[ item ]);
        const endPos = text.indexOf('}', startPos);

        if (startPos < 0 || endPos < 0) {
          continue;
        }

        const rule = text.substr(startPos + 1, (endPos - startPos) - 1);
        // TODO: //maf message rules can be nested, so there is an issue here with expression
        const re = new RegExp('\\{' + rule + '\\}', 'g');
        let formattedValue = this.formatValue({ value, rule, fieldData });
        if (typeof formattedValue === 'string' && (formattedValue as string).indexOf('$') > 0) {
          // the formatted value has a $ in it, which is a special character for regex replacement.
          // In regex, $$ in the formattedValue means the $ is escaped, so need to change the
          // single $ into $$ (which needs to have a replaceValue of $$$ as the first $ is
          // the escape character, so $$ will be in the formatted value)
          formattedValue = formattedValue.replace(/\$/g, '$$$');
        }
        text = text.replace(re, formattedValue);
      }
    }

    return text;
  }

  private formatValue(params: any) {
    // params = value, rule, fieldData
    const value = params.value;
    const rule = params.rule;
    const fieldData = params.fieldData;

    let type: string = null;
    let style: any = null;
    const aryParts = rule.split(',');
    // TODO: technical debt: could be an issue if embedded commas are in a SubformatPattern

    if (aryParts.length === 1) {
      return this.getValue({ value, fieldData });
    }

    type = aryParts[ 1 ];

    if (aryParts.length === 3) {
      style = aryParts[ 2 ];
    }

    switch (type) {
      case 'number':
        return this.formatNumber({ value, style, fieldData });

      case 'date':
        return this.formatDate({ value, style, fieldData });

      case 'time':
        return this.formatTime({ value, style, fieldData });

      case 'choice':
        return this.formatChoice({ value, style, fieldData });
    }

    // safety
    return this.getValue({ value, fieldData });
  }

  private getValue(params: any) {
    // params = value, fieldData
    const value = params.value;
    const fieldData = params.fieldData;

    if (fieldData && fieldData.specField && fieldData.specField.stateValues) {
      // let item = this.findStateItem({ value: value, specField: fieldData.specField });
      const item = this.findStateItem({ value, stateValues: fieldData.specField.stateValues });
      if (item) {
        return item.label;
      }
    }

    /*
     * NOTE: the other half of this issue is in JsonConverter.
     *
     * LMCLIENT-4777 - List column showing months in 1-12 instead of Jan - Dec
     * Since we had to place the static state values for an ArrayField in the dataView this code is required
     * to get the state for the value of the field. We really want to have this as specFields going forward
     * as that would be symmetrical with how forms work.
     */
    if (fieldData && fieldData.field && fieldData.field.stateValues) {
      // let item = this.findStateItem({ value: value, field: fieldData.field });
      const item = this.findStateItem({ value, stateValues: fieldData.field.stateValues });
      if (item) {
        return item.label;
      }
    }

    return value;
  }

  public findStateItem(params: any) {
    // params = value, specField
    const value = params.value;
    const stateValues = params.stateValues;
    // let field = params.specField;

    if (stateValues) {
      for (let index = 0, len = stateValues.length; index < len; index++) {
        const item = stateValues[ index ];
        // using non-strict equality to evaluate 0, '0', 0.00, '0.00' as zero
        // eslint-disable-next-line eqeqeq
        if (value == item.value) {
          return item;
        }
      }
    }
    return null;
  }

  private formatNumber(params: any) {
    // params = value, style, fieldData
    // style = none, integer, currency, percent, SubFormatPattern
    const value = params.value;
    const style = params.style;
    const fieldData = params.fieldData;

    // TODO: technical debt: need to process SubformatPattern
    // TODO: //maf seeing '#' as style rather than percent, currency, or integer

    // precision comes from dataView | model
    const precision = (fieldData.field && fieldData.field.precision)
      ? fieldData.field.precision
      : undefined;

    // decSize comes from specField
    const decSize = (fieldData.specField && fieldData.specField.decSize)
      ? fieldData.specField.decSize
      : undefined;

    let displayValue: string;

    // exception case where numeric would NOT be displayed as blank
    // TODO: restructure if block?
    if (style) {
      if (style === 'percent') {
        const transformOptions: LMTransformOptions = {
          precision,
          displayZero: true
        };
        displayValue = this.percentToDisplay(String(value), transformOptions);

      } else if (style === 'currency') {
        const transformOptions: LMTransformOptions = {
          precision,
          decSize,
          displayZero: true
        };
        displayValue = this.bcdToDisplay(String(value), transformOptions);

      } else {
        const transformOptions: LMTransformOptions = {
          displayZero: true
        };
        displayValue = this.numericToDisplay(value, transformOptions);
      }
    } else {
      if (precision === undefined) {
        const transformOptions: LMTransformOptions = {
          displayZero: true
        };
        displayValue = this.numericToDisplay(value, transformOptions);

      } else {
        const transformOptions: LMTransformOptions = {
          precision,
          decSize,
          displayZero: true
        };
        displayValue = this.bcdToDisplay(String(value), transformOptions);
      }
    }

    return displayValue;
  }

  private formatDate(params: any) {
    // params = value, style, fieldData
    // style = none, short, medium, long, full, SubFormatPattern
    // fieldData and specField are not guaranteed
    const value = params.value;
    const style = params.style;

    // set initial values
    let pattern = style;
    let specType = AppConstants.YYYYMMDD;
    let defaultValue = this.lmLocaleUtilService.calendar.YYYYMMDD.defaultValue;

    // reset initial values based on incoming style
    switch (style) {
      case 'short':  // completely numeric, such as 12/13/1952
        pattern = Soho.Locale.calendar().dateFormat.short;
        break;

      case 'medium': // longer, such as Jan 12, 1952
        pattern = Soho.Locale.calendar().dateFormat.medium;
        break;

      case 'long': // longer, such as January 12, 1952 or 3:30:32pm
        pattern = Soho.Locale.calendar().dateFormat.long;
        break;

      case 'full': // completely specified, such as Tuesday, April 12, 1952 AD or 3:30:42pm PST.
        pattern = Soho.Locale.calendar().dateFormat.full;
        break;

      case 'MMMM d': // anniversary
        pattern = Soho.Locale.calendar().dateFormat.month;
        defaultValue = this.lmLocaleUtilService.calendar.MMDD.defaultValue;
        specType = AppConstants.MMDD;
        break;

      case 'MMMM yyyy': // period
        pattern = Soho.Locale.calendar().dateFormat.year;
        defaultValue = this.lmLocaleUtilService.calendar.YYYYMM.defaultValue;
        specType = AppConstants.YYYYMM;
        break;

      case 'yyyy': // year - use literal value as format not defined in Locale
        pattern = 'yyyy';
        defaultValue = this.lmLocaleUtilService.calendar.YYYY.defaultValue;
        specType = AppConstants.YYYY;
        break;
    }

    if (value === 'current') {
      return this.dateTimeDisplayRule(new Date(), value, pattern, defaultValue, specType);
    }

    if (this.lmFieldUtilService.isBlank(specType, value)) {
      return '';
    }

    // TODO: technical debt: need to process SubformatPattern
    let yyyy; let mm; let dd; let hr; let mn; let sc;
    const isDateTime = (value.length === 16);

    if (value.length === 8 || isDateTime) {
      // 8 => date; 16 => datetime
      yyyy = parseInt(value.substr(0, 4), 10);
      mm = parseInt(value.substr(4, 2), 10);
      dd = parseInt(value.substr(6, 2), 10);

      if (isDateTime) {
        hr = value.substr(8, 2);
        mn = value.substr(10, 2);
        sc = value.substr(12, 2);
      }

    } else if (value.length === 6) {
      // period
      yyyy = parseInt(value.substr(0, 4), 10);
      mm = parseInt(value.substr(4, 2), 10);
      dd = 1;

    } else if (value.length === 4) {
      // anniversary or year
      mm = parseInt(value.substr(0, 2), 10);

      if (!inRange(mm, 1, 13)) {
        // is year
        yyyy = value;
        mm = 1;
        dd = 1;

      } else {
        // find closest leap year to get valid leap day (2/29) - soho does this for Soho.Locale.parseDate()
        yyyy = this.closestLeap();
        dd = parseInt(value.substr(2, 2), 10);
      }
    }

    const dt = (isDateTime)
      ? new Date(Date.UTC(yyyy, mm - 1, dd, hr, mn, sc))
      : new Date(yyyy, mm - 1, dd);

    if (!this.isDate(dt)) {
      return '';
    }

    return this.dateTimeDisplayRule(dt, value, pattern, defaultValue, specType);
  }

  // copied and modified from sohoxi locale.js
  private isLeap = y => ((y % 4 === 0) && (y % 100 !== 0)) || (y % 400 === 0);
  private closestLeap = (y?: number) => {
    let closestLeapYear = typeof y === 'number' && !isNaN(y) ? y : (new Date()).getFullYear();
    for (let i2 = 0; i2 < 4; i2++) {
      if (this.isLeap(closestLeapYear)) {
        break;
      }
      closestLeapYear--;
    }
    return closestLeapYear;
  };

  private formatTime(params: any) {
    // params = value, style, fieldData
    // style = none, short, medium, long, full, SubFormatPattern
    // fieldData and specField are not guaranteed
    const value = params.value;
    const style = params.style;

    let specType;
    const pattern = (style === 'long' || style === 'full')
      ? Soho.Locale.calendar().dateFormat.timestamp
      : Soho.Locale.calendar().timeFormat;

    if (value === 'current') {
      return this.dateTimeDisplayRule(new Date(), value, pattern,
        this.lmLocaleUtilService.calendar.TIME.defaultValue, AppConstants.TIME);
    }

    // TODO: technical debt: need to process SubformatPattern
    const isDateTime = (value.length === 16);

    // eg 2016111719421600
    const yyyy = (isDateTime) ? value.substr(0, 4) : '';
    const mm = (isDateTime) ? value.substring(4, 6) : '';
    const dd = (isDateTime) ? value.substring(6, 8) : '';

    // if a datetime, then start at 8; otherwise a time so start at zero
    const start = (isDateTime) ? 8 : 0;
    const hr = value.substr(start, 2);
    const mn = value.substr(start + 2, 2);
    const sc = value.substr(start + 4, 2);

    const dt = new Date();
    dt.setHours(hr, mn, sc);

    if (!this.isDate(dt)) {
      return '';
    }

    if (isDateTime) {
      return this.dateTimeDisplayRule(new Date(Date.UTC(yyyy, mm - 1, dd, hr, mn, sc)), value, pattern,
        this.lmLocaleUtilService.calendar.TIME.defaultValue, AppConstants.TIME);
    }

    return this.dateTimeDisplayRule(dt, value, pattern,
      this.lmLocaleUtilService.calendar.TIME.defaultValue, AppConstants.TIME);
  }

  private formatChoice(params: any) {
    // params = value, style, fieldData
    // style > SubFormatPattern
    const value = params.value;
    const style = params.style;
    const fieldData = params.fieldData;

    // TODO: technical debt: need to process choice and SubformatPattern
    const msg = (fieldData) ? '; fieldId=' + fieldData.fieldId : '';
    this._logger.log('Encountered message rule with choice type; value=' + value + '; style=' + style + msg);
    return value;
  }

  public toImageUrlValue(value: any, lines?: string, perspective?: string): SafeUrl {
    let imageUrl: SafeUrl = null;

    if (value === '') {
      value = '../lib/landmark/images/missing-picture-96px.png';
      return this.domSanitizer.bypassSecurityTrustUrl(value);
    }

    if (value !== undefined) {

      // only supporting the full perspective; remove size parameter and add perspective parameter
      if (perspective && perspective === 'full') {
        value = this.lmUrlUtilService.removeParam(value, 'size');
        value = this.lmUrlUtilService.addParamToUrl(value, 'perspective', perspective);
      }

      // lines can be used with the size parameter or the perspective parameter
      if (lines) {
        value = this.lmUrlUtilService.addParamToUrl(value, 'lines', lines);
      }

      // add all the csk values
      value = this.lmUrlUtilService.updateContextSessionKeyParams(value);
      imageUrl = this.domSanitizer.bypassSecurityTrustUrl(value);
    }

    return imageUrl;
  }

  public stringToElement(markupString: any): Element {
    const parser = new DOMParser();
    const html = parser.parseFromString(markupString, 'text/html');
    return html.body.firstElementChild;
  }

  /**
   * Determines how the rating control should display and returns the formatted array
   *
   * @param value - number value that determines how many stars are filled
   * @param id - string id to be used to determine the id for each option
   * @returns Array<LMStarRatingOption> - array to be used in the template to display the control
   */
  public formatStarRatingOptions(value: number, id: string): Array<LMStarRatingOption> {
    const options = this.uniqueStarRatings();

    if (value === undefined) {
      // safety
      value = 0;
    }

    if (value <= .2) {
      // zero stars
      this.fillOrClearAllStars(false, options, id);
    } else if (.2 < value && value < 0.8) {
      // half star
      this.fillStars(0, true, options, id);
    } else if (.8 <= value && value <= 1.2) {
      // one star
      this.fillStars(1, false, options, id);
    } else if (1.2 < value && value < 1.8) {
      // one and a half stars
      this.fillStars(1, true, options, id);
    } else if (1.8 <= value && value <= 2.2) {
      // two stars
      this.fillStars(2, false, options, id);
    } else if (2.2 < value && value < 2.8) {
      // two and a half stars
      this.fillStars(2, true, options, id);
    } else if (2.8 <= value && value <= 3.2) {
      // three stars
      this.fillStars(3, false, options, id);
    } else if (3.2 < value && value < 3.8) {
      // three and half stars
      this.fillStars(3, true, options, id);
    } else if (3.8 <= value && value <= 4.2) {
      // four stars
      this.fillStars(4, false, options, id);
    } else if (4.2 < value && value < 4.8) {
      // four and half stars
      this.fillStars(4, true, options, id);
    } else if (4.8 <= value) {
      // five stars
      this.fillOrClearAllStars(true, options, id);
    }

    return options;
  }

  /**
   * Set all stars options to either fill or empty
   *
   * @param fillStars - boolean on if the stars should be filled or empty
   * @param options - the array of options to modify
   * @param id - id to append to the start of the ids in the options array
   * @private
   */
  private fillOrClearAllStars(fillStars: boolean, options: Array<LMStarRatingOption>, id: string) {
    const starClass = fillStars ? 'is-filled' : '';
    const href = fillStars ? '#icon-star-filled' : '#icon-star-outlined';
    options.forEach(option => {
      option.class = starClass;
      option.href = href;
      option.id = id + option.id;
    });
  }

  /**
   * Sets the star options to be filled, empty or half.
   *
   * @param starFillNumber - number of stars to fill in
   * @param andAHalf - boolean if there is a half more star that should be filled in (so 3.5 stars).
   * @param options - the options array to modify the values for
   * @param id - the id to append to the option id
   * @private
   */
  private fillStars(starFillNumber: number, andAHalf: boolean, options: Array<LMStarRatingOption>, id: string): void {
    // Since the starFillNumber corresponds to the actual star clicked, and javascript indexes from 0
    // the indexes less than the star number should be filled and the indexes greater than and equal
    // to the starFillNumber should be empty.
    // Then if there should be a half star, that half will be the index of the starFillNumber as long
    // as this doesn't exceed 5
    options.forEach((option: LMStarRatingOption, index: number) => {
      option.id = id + option.id;

      if (index < starFillNumber) {
        option.class = 'is-filled';
        option.href = '#icon-star-filled';
      } else if (andAHalf && starFillNumber < 5 && starFillNumber === index) {
        option.class = 'is-half';
        option.href = '#icon-star-half';
      } else {
        option.class = 'lm-is-outline';
        option.href = '#icon-star-outlined';
      }
    });
  }

  private displayNegativeParens(value: string, transformOptions: LMTransformOptions): string {
    if (transformOptions && transformOptions.displayNegativeParens) {
      const minusSign: string = Soho.Locale.numbers().minusSign;
      const isLTRNegative: boolean = value.charAt(0) === minusSign || false;
      const isRTLNegative: boolean = value.charAt(value.length - 1) === minusSign || false;

      if (isLTRNegative) {
        value = [ '(', value.substring(1, value.length), ')' ].join('');
      } else if (isRTLNegative) {
        value = [ '(', value.substring(-1, value.length - 1), ')' ].join('');
      }
    }

    return value;
  }

  private displayAmountRelative(value: string, transformOptions: LMTransformOptions): string {
    if (transformOptions && transformOptions.displayInThousands) {
      value = String(Math.round(Soho.Locale.parseNumber(value) / 1000));
    } else if (transformOptions && transformOptions.displayInMillions) {
      value = String(Math.round(Soho.Locale.parseNumber(value) / 1000000));
    }

    return value;
  }

  private uniqueStarRatings(): Array<LMStarRatingOption> {
    return JSON.parse(JSON.stringify(this.ratingOptions));
  }

  private getTimeZoneShortName(timezone: string): string {
    const options = {
      year: 'numeric',
      hourCycle: 'h24',
      timeZoneName: 'short'
    } as Intl.DateTimeFormatOptions;

    if (timezone) {
      options.timeZone = timezone;
    }

    const dateTimeArray = new Intl.DateTimeFormat(Soho.Locale.currentLocale.name, options).format(new Date()).split(' ');
    // there are locales that has 3 elements where the last element is the time zone
    // i.e. fi = '2022 klo UTC+10', bg = '2022 г., Гринуич+10', th = พ.ศ. 2565 GMT+10
    let timeZoneShortName = dateTimeArray?.[2] || dateTimeArray?.[1];

    // for locales such as vi-VN that has an interchanged format
    // i.e. vi = 'GMT+10, 2022'
    if (Number(timeZoneShortName) === new Date().getFullYear()) {
      timeZoneShortName = (dateTimeArray?.[0] || '').substring(0, (dateTimeArray?.[0] || '').length-1);
    }

    return timeZoneShortName || '';
  }

  public getCurrencySymbol(currencySymbolInfo: LMCurrencySymbolInfo, dataView: LMDataView): string {
    let currencySymbol;

    if (currencySymbolInfo.isRelatedField) {
      const field = dataView.fields[ currencySymbolInfo.currencySymbol ];
      if (field != null) {
        currencySymbol = field.value;
      }
    } else {
      currencySymbol = currencySymbolInfo.currencySymbol;
    }

    return currencySymbol;
  }

  public getMultipleFilterFieldValue(value: string, type: string, decSize?: number, precision?: number, allowZero?: boolean): LMMultipleFilterFieldValue {
    let formattedFilterValue;
    let transformOptions: LMTransformOptions;

    const dblQuotedTermsExp = new RegExp('(?:\\B"[^"]*)\\' + AppConstants.MULTI_SEARCH_TERM_DELIMITER + '(?:[^"]*"\\B)', 'g');
    const dblQuotedTerms = value.match(dblQuotedTermsExp);

    const unQuotedTermsExp = new RegExp('(?:\\B"[^"]*)\\' + AppConstants.MULTI_SEARCH_TERM_DELIMITER + '(?:[^"]*"[,]*\\B)', 'g');
    const unQuotedTerms = value.replace(unQuotedTermsExp, '').trim();

    const unQuotedTermsAry = unQuotedTerms.length > 0
      ? unQuotedTerms.split(AppConstants.MULTI_SEARCH_TERM_DELIMITER).filter(term => term.trim().length > 0)
      : [];


    // make sure blank values are filtered out for non numeric, bcd, percent fields
    const valuesArray = unQuotedTermsAry.filter((memberValue) => {
      if (allowZero) {
        return memberValue;
      } else {
        return !this.lmFieldUtilService.isBlank(type, memberValue);
      }
    });


    let combinedValues = !dblQuotedTerms ? valuesArray : dblQuotedTerms.concat(valuesArray);

    if (type === AppConstants.BCD
        || type === AppConstants.PERCENT) {
      transformOptions = {
        decSize,
        precision
      };

      // since BCD field, make sure the correct decSize and precision for display
      if (type === AppConstants.BCD) {
        combinedValues = combinedValues.map(itemValue => {
          transformOptions.allowMultiSelections = true;
          return this.toDataValue(itemValue, type, transformOptions);
        });
      }

      formattedFilterValue = combinedValues.map(itemValue => {
        // TODO since " is no longer used for BCD/PERCENT (since , isn't the multivalue delimiter), do we need this line?
        itemValue = itemValue.replace(/"/g, '');
        return this.toDataValue(itemValue, type, transformOptions);
      });
    } else {
      formattedFilterValue = combinedValues.map(itemValue =>
        this.toDataValue(itemValue, type, transformOptions));
    }

    const enteredFilterValue = combinedValues;

    return { formattedFilterValue, enteredFilterValue };
  }

  /**
   * Util to remove duplicate objects in an array
   * @param arr array of objects
   * @param key key to check against, key must be in the object inside arr
   * @returns unique array of objects
   */
  getUniqueListByKey<T, K extends keyof T>(arr: T[], key: K): T[] {
    return [ ...new Map(arr.map(item => [ item[key], item ]))?.values() ] as T[];
  }
}
